import Icon from '../../../../Icon/Icon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TableDragSelect from 'react-table-drag-select';

const CustomTableDragSelect = (props) => {
    const { t } = useTranslation();
    const { change, readOnly = false, campaign, timing } = props;

    const selectorTiming = useSelector((state) => timing);

    const hoursString = useMemo(() => {
        if (campaign) {
            if (
                typeof campaign.hours_enabled === 'string' &&
                campaign.hours_enabled.length > 0
            ) {
                return campaign.hours_enabled;
            }
            if (
                typeof campaign.hoursEnabled === 'string' &&
                campaign.hoursEnabled.length > 0
            ) {
                return campaign.hoursEnabled;
            }
            return '';
        } else {
            if (
                selectorTiming &&
                typeof selectorTiming.hoursEnabled === 'string'
            ) {
                return selectorTiming.hoursEnabled;
            }
            return '';
        }
    }, [campaign, selectorTiming]);

    // Build initial matrix from hoursString (7 x 24)
    const buildMatrix = useCallback((str) => {
        const matrix = [];
        for (let day = 0; day < 7; day++) {
            const row = [];
            for (let hour = 0; hour < 24; hour++) {
                const index = day * 24 + hour;
                const isSelected =
                    str && str.length > index && str[index] === '1';
                row.push(!!isSelected);
            }
            matrix.push(row);
        }
        return matrix;
    }, []);

    // Keep cells in state so interactions aren't working with a stale memo
    const [cells, setCells] = useState(() => buildMatrix(hoursString));

    // When hoursString changes (new campaign or selector update), update cells
    useEffect(() => {
        setCells(buildMatrix(hoursString));
    }, [hoursString, buildMatrix]);

    const updateHoursEnabled = (newCells) => {
        if (readOnly) return;
        const hoursEnabledString = newCells
            .flat()
            .map((cell) => (cell ? '1' : '0'))
            .join('');
        change('hoursEnabled', hoursEnabledString);
        setCells(newCells);
    };

    const fillRow = (row) => {
        if (readOnly) return;
        setCells((prev) => {
            const newCells = prev.map((cellRow, rowIndex) => {
                if (rowIndex === row) {
                    const isAlreadyFill = cellRow.every((cell) => cell);
                    return cellRow.map(() => !isAlreadyFill);
                }
                return [...cellRow];
            });
            updateHoursEnabled(newCells);
            return newCells;
        });
    };

    const fillCol = (col) => {
        if (readOnly) return;
        setCells((prev) => {
            const isAlreadyFill = prev.every((row) => row[col]);
            const newCells = prev.map((cellRow) =>
                cellRow.map((cell, colIndex) =>
                    colIndex === col ? !isAlreadyFill : cell
                )
            );
            updateHoursEnabled(newCells);
            return newCells;
        });
    };

    const handleCellChange = (newCells) => {
        if (readOnly) return;
        updateHoursEnabled(newCells);
    };

    const highlightRowAndCol = (rowIndex) => {
        let row = Math.floor(rowIndex / 24);
        let col = rowIndex % 24;
    }

    return (
        <div className="block my-2 overflow-x-auto w-full">
            <div className="flex flex-row flex-shrink-0 justify-start mt-2 text-xs font-mono w-full">
                <div className="rounded my-1 mr-3 w-3 h-3 flex-shrink-0 bg-gray-100 text-gray-500 text-center">
                    <Icon name="calendar-alt" />
                </div>
                {[...Array(24)].map((_, hour) => (
                    <div
                        key={hour}
                        onClick={() => !readOnly && fillCol(hour)}
                        className={`rounded my-1 mr-2 w-4 h-4 flex-shrink-0 bg-gray-100 text-gray-500 text-center ${!readOnly ? 'cursor-pointer hover:bg-gray-200' : ''
                            }`}>
                        {hour}
                    </div>
                ))}
            </div>
            <div className="flex justify-start">
                <div className="flex flex-col my-2 mr-2">
                    {[
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.sunday-letter'
                        ),
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.monday-letter'
                        ),
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.tuesday-letter'
                        ),
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.wednesday-letter'
                        ),
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.thursday-letter'
                        ),
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.friday-letter'
                        ),
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.saturday-letter'
                        ),
                    ].map((dayLetter, dayIndex) => (
                        <div
                            key={dayIndex}
                            onClick={() => !readOnly && fillRow(dayIndex)}
                            className={`rounded mb-3 w-4 h-4 bg-gray-100 text-gray-500 font-semibold text-xs text-center ${!readOnly
                                ? 'cursor-pointer hover:bg-gray-200'
                                : ''
                                }`}>
                            {dayLetter}
                        </div>
                    ))}
                </div>
                <div className="flex justify-start">
                    <div className={`${readOnly ? 'pointer-events-none' : ''}`}
                    >
                        <TableDragSelect
                            value={cells}
                            onChange={handleCellChange}
                            tabindex="0"
                            disabled={readOnly}
                            className="table-drag-select"
                            data-testid="custom-table-drag-select">
                            {cells.map((row, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    className="my-1 flex justify-start">
                                    {row.map((cell, colIndex) => (
                                        <td
                                            key={colIndex}
                                            className={`cursor-pointer rounded-sm m-1 w-4 h-4 ${cell
                                                ? 'bg-blue-500'
                                                : ''
                                                }
                                                `
                                            }
                                            onHover={() => highlightRowAndCol(rowIndex)}
                                        />
                                    ))}
                                </tr>
                            ))}
                        </TableDragSelect>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default CustomTableDragSelect;
